// src/components/auth/AuthPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../firebase/AuthContext';
import Modal from '../ui/Modal';
import InputField from '../ui/InputField';
import AnimatedCharacter from '../ui/AnimatedCharacter';
import { Eye, EyeClosed } from 'lucide-react';

const AuthPage = ({ onAuthSuccess }) => {
  // View state
  const [view, setView] = useState('login'); // 'login', 'signup', 'forgot-password', 'verify-email'

  // Form fields
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // UI state
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [microsoftLoading, setMicrosoftLoading] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  // Validation state
  const [emailTouched, setEmailTouched] = useState(false);
  const [emailValid, setEmailValid] = useState(true);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [passwordValid, setPasswordValid] = useState(true);
  const [passwordsMatch, setPasswordsMatch] = useState(true);

  const { login, signup, signInWithGoogle, signInWithMicrosoft, resetPassword, resendVerificationEmail, refreshUser, currentUser } = useAuth();

  // Blink sequence for AnimatedCharacter (static mode)
  const blinkSequence = [
    {
      x: 0,
      y: 0,
      duration: 0.1,
      pause: 200,
      postAnimationPause: 0,
      specialAnimation: 'blink'
    },
    {
      x: 0,
      y: 0,
      duration: 0.1,
      pause: 5000,
      postAnimationPause: 0,
      specialAnimation: null
    }
  ];

  // Force repaint to fix autofill rendering bug
  useEffect(() => {
    // Trigger a style recalculation after mount
    const timer = setTimeout(() => {
      void document.body.offsetHeight; // Forces reflow
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Check if user needs email verification
  useEffect(() => {
    if (currentUser) {
      console.log('AuthPage - Current user:', currentUser.uid, 'Email verified:', currentUser.emailVerified);
      
      if (!currentUser.emailVerified) {
        console.log('AuthPage - Email not verified, showing verification view');
        setView('verify-email');
      } else {
        console.log('AuthPage - Email verified, proceeding to app');
        onAuthSuccess();
      }
    }
  }, [currentUser, onAuthSuccess]);

  // Enhanced email validation
  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email) && email.length <= 254 && !email.startsWith('.') && !email.endsWith('.');
  };

  // Email blur handler
  const handleEmailBlur = () => {
    setEmailTouched(true);
    setEmailValid(validateEmail(email));
  };

  // Password blur handler
  const handlePasswordBlur = () => {
    setPasswordTouched(true);
    setPasswordValid(password.length >= 8);
  };

  // Live validation - clear errors once fixed
  useEffect(() => {
    if (emailTouched && validateEmail(email)) {
      setEmailValid(true);
    }
  }, [email, emailTouched]);

  useEffect(() => {
    if (passwordTouched && password.length >= 8) {
      setPasswordValid(true);
    }
  }, [password, passwordTouched]);

  useEffect(() => {
    if (confirmPassword) {
      setPasswordsMatch(password === confirmPassword);
    }
  }, [password, confirmPassword]);

  // Handle checking verification status
  const handleCheckVerification = useCallback(async () => {
    try {
      setError('');
      setLoading(true);
      await refreshUser();
      
      if (currentUser?.emailVerified) {
        onAuthSuccess();
      } else {
        setError('Email not yet verified. Please check your inbox.');
      }
    } catch (err) {
      setError('Failed to check verification status. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [refreshUser, currentUser, onAuthSuccess]);

  // Listen for email verification completion from other tabs
  useEffect(() => {
    if (view === 'verify-email') {
      const channel = new BroadcastChannel('email-verification');
      
      channel.onmessage = async (event) => {
        if (event.data === 'verified') {
          console.log('AuthPage - Received verification notification from another tab');
          await handleCheckVerification();
        }
      };
      
      return () => {
        console.log('AuthPage - Closing BroadcastChannel');
        channel.close();
      };
    }
  }, [view, handleCheckVerification]);

  // Format Firebase error messages to be user-friendly
  const formatErrorMessage = (err) => {
    const message = err.message || err.toString();
    
    const errorMap = {
      'auth/invalid-credential': 'Invalid email or password. Please check your credentials and try again.',
      'auth/user-not-found': 'No account found with this email address.',
      'auth/wrong-password': 'Incorrect password. Please try again.',
      'auth/email-already-in-use': 'An account with this email already exists.',
      'auth/weak-password': 'Password should be at least 8 characters.',
      'auth/invalid-email': 'Please enter a valid email address.',
      'auth/operation-not-allowed': 'This sign-in method is not enabled. Please contact support.',
      'auth/user-disabled': 'This account has been disabled. Please contact support.',
      'auth/too-many-requests': 'Too many failed attempts. Please try again later or reset your password.',
      'auth/network-request-failed': 'Network error. Please check your connection and try again.',
    };

    for (const [code, friendlyMessage] of Object.entries(errorMap)) {
      if (message.includes(code)) {
        return friendlyMessage;
      }
    }

    return message
      .replace(/^Firebase:\s*/i, '')
      .replace(/\s*\([^)]*\)\.?$/, '')
      .trim();
  };

  // Handle email/password login
  const handleLogin = async (e) => {
    e.preventDefault();
    console.log('AuthPage - Attempting email/password login');

    try {
      setError('');
      setLoading(true);
      await login(email, password);
      console.log('AuthPage - Email/password login successful');
    } catch (err) {
      console.error('AuthPage - Email/password login failed:', err.message);
      setError(formatErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Handle email/password signup
  const handleSignup = async (e) => {
    e.preventDefault();

    let hasError = false;

    if (!validateEmail(email)) {
      setEmailTouched(true);
      setEmailValid(false);
      hasError = true;
    }

    if (password.length < 8) {
      setPasswordTouched(true);
      setPasswordValid(false);
      hasError = true;
    }

    if (password !== confirmPassword) {
      setPasswordsMatch(false);
      hasError = true;
    }

    if (hasError) {
      return;
    }

    try {
      setError('');
      setLoading(true);
      await signup(email, password);
      console.log('AuthPage - Signup successful, verification email sent');
    } catch (err) {
      setError(formatErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Handle Google sign-in
  const handleGoogleSignIn = async () => {
    console.log('AuthPage - Attempting Google sign-in');

    try {
      setError('');
      setGoogleLoading(true);
      await signInWithGoogle();
      console.log('AuthPage - Google sign-in successful');
    } catch (err) {
      console.error('AuthPage - Google sign-in failed:', err.message);
      setError(formatErrorMessage(err));
    } finally {
      setGoogleLoading(false);
    }
  };

  // Handle Microsoft sign-in
  const handleMicrosoftSignIn = async () => {
    console.log('AuthPage - Attempting Microsoft sign-in');

    try {
      setError('');
      setMicrosoftLoading(true);
      await signInWithMicrosoft();
      console.log('AuthPage - Microsoft sign-in successful');
    } catch (err) {
      console.error('AuthPage - Microsoft sign-in failed:', err.message);
      setError(formatErrorMessage(err));
    } finally {
      setMicrosoftLoading(false);
    }
  };

  // Handle password reset request
  const handlePasswordReset = async (e) => {
    e.preventDefault();

    try {
      setError('');
      setLoading(true);
      await resetPassword(email);
      setResetEmailSent(true);
    } catch (err) {
      setError(formatErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Handle resend verification email
  const handleResendVerification = async () => {
    try {
      setError('');
      setLoading(true);
      await resendVerificationEmail();
      setError(''); // Clear any previous errors
    } catch (err) {
      if (err.message.includes('already verified')) {
        await handleCheckVerification();
      } else {
        setError('Failed to resend verification email. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Reset form when switching views
  const switchView = (newView) => {
    setView(newView);
    setError('');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setResetEmailSent(false);
    setEmailTouched(false);
    setEmailValid(true);
    setPasswordTouched(false);
    setPasswordValid(true);
    setPasswordsMatch(true);
  };

  // Render content based on current view
  const renderModalContent = () => {
    // Login View
    if (view === 'login') {
      return (
        <>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-app-lg py-app-md rounded-app-md mb-app-xl text-app-sm">
              {error}
            </div>
          )}

          <button
            onClick={handleGoogleSignIn}
            disabled={googleLoading || loading || microsoftLoading}
            className="w-full mb-app-lg bg-app-white hover:bg-app-gray-300 text-app-gray-900 font-medium text-app-sm py-app-md px-app-lg border-[1px] border-app-gray-900 rounded-app-md flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-app-normal"
          >
            {googleLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-1 border-app-gray-900 mr-2"></div>
            ) : (
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            )}
            {googleLoading ? 'Signing in...' : 'Continue with Google'}
          </button>

          <button
            onClick={handleMicrosoftSignIn}
            disabled={microsoftLoading || loading || googleLoading}
            className="w-full mb-app-lg bg-app-white hover:bg-app-gray-300 text-app-gray-900 font-medium text-app-sm py-app-md px-app-lg border-[1px] border-app-gray-900 rounded-app-md flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-app-normal"
          >
            {microsoftLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-1 border-app-gray-900 mr-2"></div>
            ) : (
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#f25022" d="M11.4 11.4H2V2h9.4v9.4z"/>
                <path fill="#00a4ef" d="M22 11.4h-9.4V2H22v9.4z"/>
                <path fill="#7fba00" d="M11.4 22H2v-9.4h9.4V22z"/>
                <path fill="#ffb900" d="M22 22h-9.4v-9.4H22V22z"/>
              </svg>
            )}
            {microsoftLoading ? 'Signing in...' : 'Continue with Microsoft'}
          </button>

          <div className="relative mb-app-lg">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-app-gray-400"></div>
            </div>
            <div className="relative flex justify-center text-app-xs">
              <span className="px-app-md bg-app-white text-app-gray-500">Or continue with email</span>
            </div>
          </div>

          <form onSubmit={handleLogin} noValidate>
            <div className="space-y-app-xs">
              <InputField
                id="email"
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={handleEmailBlur}
                required
                disabled={loading || googleLoading}
                validationMessage="Please enter a valid email address"
                validationMode="inline" // Only shows when invalid
                validationCheck="blur" // Check as you type
                isValid={emailValid || email === ''}
              />

              <InputField
                id="password"
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading || googleLoading}
                showPasswordToggle={true}
              />

            </div>

            <button
              onClick={() => switchView('forgot-password')}
              className="text-app-gray-400 text-app-2xs hover:text-app-gray-900 transition-colors mt-app-sm"
            >
              Forgot password?
            </button>

            <button
              type="submit"
              className="btn-primary w-full mt-app-xl disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || googleLoading || !email || !password || (emailTouched && !emailValid)}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="flex justify-center items-center mt-app-xl text-app-xs group">
            <button
              onClick={() => switchView('signup')}
              className="text-app-gray-500 transition-colors font-medium"
            >
              No account yet? <span className="text-app-accent group-hover:text-app-accent-300 transition-colors font-semibold">Sign up here</span>
            </button>
          </div>
        </>
      );
    }

    // Signup View
    if (view === 'signup') {
      return (
        <>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-app-lg py-app-md rounded-app-md mb-app-xl text-app-sm">
              {error}
            </div>
          )}

          <button
            onClick={handleGoogleSignIn}
            disabled={googleLoading || loading || microsoftLoading}
            className="w-full mb-app-lg bg-app-white hover:bg-app-gray-300 text-app-gray-900 font-medium text-app-sm py-app-md px-app-lg border border-app-gray-900 rounded-app-md flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-app-normal"
          >
            {googleLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-app-gray-900 mr-2"></div>
            ) : (
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
            )}
            {googleLoading ? 'Signing in...' : 'Continue with Google'}
          </button>

          <button
            onClick={handleMicrosoftSignIn}
            disabled={microsoftLoading || loading || googleLoading}
            className="w-full mb-app-lg bg-app-white hover:bg-app-gray-300 text-app-gray-900 font-medium text-app-sm py-app-md px-app-lg border border-app-gray-900 rounded-app-md flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-app-normal"
          >
            {microsoftLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-app-gray-900 mr-2"></div>
            ) : (
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#f25022" d="M11.4 11.4H2V2h9.4v9.4z"/>
                <path fill="#00a4ef" d="M22 11.4h-9.4V2H22v9.4z"/>
                <path fill="#7fba00" d="M11.4 22H2v-9.4h9.4V22z"/>
                <path fill="#ffb900" d="M22 22h-9.4v-9.4H22V22z"/>
              </svg>
            )}
            {microsoftLoading ? 'Signing in...' : 'Continue with Microsoft'}
          </button>

          <div className="relative mb-app-lg">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-app-gray-400"></div>
            </div>
            <div className="relative flex justify-center text-app-xs">
              <span className="px-app-md bg-app-white text-app-gray-500">Or sign up with email</span>
            </div>
          </div>

          <form onSubmit={handleSignup} noValidate>
            <div className="space-y-app-xs">
              <InputField
                id="email"
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={handleEmailBlur}
                placeholder="you@domain.com"
                required
                disabled={loading || googleLoading}
                validationMessage="Please enter a valid email address"
                validationMode="inline" // Only shows when invalid
                validationCheck="blur" // Check as you type
                isValid={emailValid || email === ''}
              />

              <InputField
                id="password"
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onBlur={handlePasswordBlur}
                required
                disabled={loading || googleLoading}
                showPasswordToggle={true}
                validationMessage="Password must be at least 8 characters"
                validationMode="permanent" // Always shows, gray when valid, red when invalid
                validationCheck="blur" // Check on blur, clear live once valid
                isValid={passwordValid || password === ''}
              />

              <InputField
                id="confirmPassword"
                label="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading || googleLoading}
                showPasswordToggle={true}
                validationMessage="Passwords do not match"
                validationMode="inline" // Only shows when invalid
                validationCheck="live" // Check as you type
                isValid={passwordsMatch || confirmPassword === ''}
              />
            </div>

            <button
              type="submit"
              className="btn-primary w-full mt-app-lg disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading || googleLoading || !email || !password || !confirmPassword || (emailTouched && !emailValid) || (passwordTouched && !passwordValid) || !passwordsMatch}
            >
              {loading ? 'Creating Account...' : 'Sign Up'}
            </button>
          </form>

          <div className="text-center mt-app-xl text-app-xs">
            <button
              onClick={() => switchView('login')}
              className="text-app-gray-500 font-medium hover:text-app-gray-900 transition-colors"
            >
              Already have an account? Log in here
            </button>
          </div>
        </>
      );
    }

    // Forgot Password View
    if (view === 'forgot-password') {
      return (
        <>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-app-lg py-app-md rounded-app-md mb-app-xl text-app-sm">
              {error}
            </div>
          )}

          {resetEmailSent ? (
            <div className="bg-green-100 border border-green-400 text-green-700 px-app-lg py-app-md rounded-app-md mb-app-xl text-app-sm">
              If an account exists, a reset link has been sent to that inbox.
            </div>
          ) : (
            <>
              <p className="text-app-sm text-app-gray-600 mb-app-md">
                Enter your email to receive a reset link.
              </p>

              <form onSubmit={handlePasswordReset} noValidate>
                <InputField
                  id="email"
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={handleEmailBlur}
                  placeholder="Enter your email"
                  required
                  disabled={loading || googleLoading}
                  validationMessage="Please enter a valid email address"
                  validationMode="inline" // Only shows when invalid
                  validationCheck="blur" // Check as you type
                  isValid={emailValid || email === ''}
                />

                <button
                  type="submit"
                  className="btn-primary w-full mt-app-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || !email || (emailTouched && !emailValid)}
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>
            </>
          )}

          <div className="text-center mt-app-xl text-app-xs">
            <button
              onClick={() => switchView('login')}
              className="text-app-gray-500 hover:text-app-gray-900 transition-colors"
            >
              Back to login
            </button>
          </div>
        </>
      );
    }

    // Email Verification View
    if (view === 'verify-email') {
      return (
        <>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-app-lg py-app-md rounded-app-md mb-app-xl text-app-sm">
              {error}
            </div>
          )}

          <div className="bg-blue-100 border border-blue-400 text-blue-700 px-app-lg py-app-md rounded-app-md mb-app-xl text-app-sm">
            A verification email has been sent to <strong>{currentUser?.email}</strong>
          </div>

          <p className="text-app-sm text-app-gray-600 mb-app-xl">
            Once you've clicked the link you'll be logged in automatically.
          </p>

          <button
            onClick={handleResendVerification}
            className="btn-secondary w-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-app-normal"
            disabled={loading}
          >
            {loading ? 'Sending...' : 'Resend'}
          </button>

          <div className="text-center mt-app-xl text-app-xs">
            <button
              onClick={() => switchView('login')}
              className="text-app-gray-500 hover:text-app-gray-900 transition-colors"
            >
              Back to login
            </button>
          </div>
        </>
      );
    }

    return null;
  };

  // Get title based on view
  const getTitle = () => {
    switch (view) {
      case 'login':
        return 'Log in to Stampstack';
      case 'signup':
        return 'Sign up with Stampstack';
      case 'forgot-password':
        return 'Reset Password';
      case 'verify-email':
        return 'Verify Your Email';
      default:
        return 'Stampstack';
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-app-gray-700">
      <Modal
        isOpen={true}
        onClose={() => {}}
        title={getTitle()}
        showCloseButton={false}
        width="400px"
        headerContent={
          <div className="w-full h-12">
            <AnimatedCharacter
              lookAtSequence={blinkSequence}
              staticMode={true}
              className="w-full h-12"
            />
          </div>
        }
        titleAlign="center"
        titleClassName="font-semibold"
        contentPadding="px-app-3xl pb-app-2xl pt-app-xl"
      >
        {renderModalContent()}
      </Modal>
    </div>
  );
};

export default AuthPage;